/** Shipments API service. Delegates rms-api connection management
 *  (JWT, path resolution, direct vs proxy) to RmsApiConnectionManager. */
// eslint-disable-next-line
import { injectable } from "inversify";
import { TYPE, rootContainer } from '../container/root';
import { IApiResponse } from "../http-service";
import { IFetchShipmentsListArgs, IShipment } from "../../pages/shipments/shipment.types";
import { createPagedRequestURLSearchParams } from "../helpers";
import RmsApiConnectionManager from "../rms-api-connection-manager";

export interface IShipmentsService {
    getShipments: (args: IFetchShipmentsListArgs) => Promise<IShipmentsResponse>;
    getFilterOptions: () => Promise<IFilterOptionsResponse>;
}

interface IShipmentsResponse {
    data: IShipment[];
    current_page: number;
    per_page: number;
    total_records: number;
    total_pages: number;
}

interface IFilterOptionsResponse {
    carriers: string[];
    connections: IConnection[];
}

export interface IConnection {
    id: string;
    name: string;
}

@injectable()
class ShipmentsService implements IShipmentsService {
    private readonly rmsApiConnection: RmsApiConnectionManager = rootContainer.get<RmsApiConnectionManager>(TYPE.RmsApiConnectionManager);

    async getShipments(args: IFetchShipmentsListArgs): Promise<IShipmentsResponse> {
        const searchParams = createPagedRequestURLSearchParams(args.page, args.perPage, args.sortBy, args.sortDir);
        const excludedFields = ['perPage', 'page', 'sortBy', 'sortDir'];

        Object.entries(args).forEach(([key, value]) => {
            if (!excludedFields.includes(key) && value !== null && value !== undefined) {
                searchParams.append(key, value);
            }
        });
        const result = await this.rmsApiConnection.get<IApiResponse<IShipmentsResponse>>(
            `/shipments?${searchParams.toString()}`
        );
        return result.payload;
    }

    async getFilterOptions(): Promise<IFilterOptionsResponse> {
        const result = await this.rmsApiConnection.get<IApiResponse<IFilterOptionsResponse>>(
            '/shipments/filters'
        );
        return result.payload;
    }
}

export default ShipmentsService;
